import React, { useState, useEffect } from "react";
import { Constants } from "../../constants/appConstants";
import { OrdersUpdate } from "../../models/csos/ordersUpdate";
import { UpdateQuantity } from "../../models/csos/updateQuantity";
import { csosApiService } from "../../services/api/csosApiService";
import { userService } from "../../services/userService";
import { useOrderDetailsService } from "../../services/component/orderDetailsService";
import { buildModifiedOrderLines } from "../../utils/buildModifiedOrderLines";
import { fetchAndProcessOrder } from "../../utils/fetchAndProcessOrder";
import ModalContentCenter from "../modalContentCenter/ModalContentCenter";
import SignApprovedOrder from "./signApprove/SignApprovedOrder";
import "./OrderDetailFooter.css";

interface OrderDetailFooterProps {
  order: any;
  setOrder: (order: any) => void;
}

const OrderDetailFooter: React.FC<OrderDetailFooterProps> = ({ order, setOrder }) => {
  const orderDetailsService = useOrderDetailsService();
  const [loggedInUser] = useState(userService.getCurrentUser());
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showOrderTwenty, setShowOrderTwenty] = useState(false);
  const [showErrorDTK, setShowErrorDTK] = useState(false);
  const [showOverRiddenPopUp, setShowOverRiddenPopUp] = useState(false);

  useEffect(() => {
    const unsub = orderDetailsService.quantityUpdate$.subscribe((isEdited) => {
      // can store if needed
    });
    return () => unsub();
  }, [orderDetailsService]);

  const setOrderFromResponse = (response: any) => {
    setOrder({ ...order, ...response });
  };

  const fetchNextOrder = () => {
    csosApiService.getNextOrder().then((response) => {
      if (response.body && response.body.orders[0]) {
        const nextOrder = response.body.orders[0];
        nextOrder.orderLines.forEach((ol: any) => { ol.oldOrderQuantity = ol.orderQuantity; });
        orderDetailsService.announcestartTimer(nextOrder);
        setOrderFromResponse(nextOrder);
        if (nextOrder.totalNumOfApprovedOrders === 20) {
          setShowOrderTwenty(true);
        }
        const tableDiv = document.getElementById("orderDetailsTableModalId");
        if (tableDiv) tableDiv.scrollTop = 0;
      } else {
        setShowOrderTwenty(true);
      }
    }).catch(() => {
      setShowErrorDTK(true);
    });
  };

  const rejectCsosOrders = () => {
    updateQuantityReject(Constants.Operation_Name_REJECT);
  };

  const approveCsosOrders = () => {
    if (order.totalNumOfApprovedOrders < 20) {
      approveQuantity(Constants.Operation_Name_APPROVE);
    } else {
      setShowOrderTwenty(true);
      orderDetailsService.resetTimer("reset");
    }
  };

  const newCsosOrders = () => {
    if (order.status === Constants.NEW) {
      orderDetailsService.resetTimer("reset");
      cancelCsosOrders(Constants.Operation_Name_CANCEL);
    } else {
      orderDetailsService.announceDetailClose("close");
      orderDetailsService.refreshorder("refresh");
      orderDetailsService.resetTimer("reset");
    }
  };

  const overrideCsosOrders = () => {
    const ordersUpdate = new OrdersUpdate();
    ordersUpdate.operationName = Constants.Operation_Name_OVERRIDE;
    ordersUpdate.orders = [{
      customerNumber: order.customerNum,
      distributionCenter: order.dcNum,
      orderCreateSTP: "",
      orderId: order.orderId,
      uniqueTrackingNumber: "",
      uniqueTrackingNumberOld: "",
    }];
    ordersUpdate.statusCode = order.status;
    csosApiService.updateCsosOrders(ordersUpdate).then((response) => {
      if (response.body && response.body.hostMessage === "SUCCESS") {
        newOrderAfterOverride();
      } else {
        setShowOverRiddenPopUp(true);
      }
    }).catch(() => {
      setShowOverRiddenPopUp(true);
    });
  };

  const newOrderAfterOverride = () => {
    setShowErrorDTK(false);
    setShowOverRiddenPopUp(false);
    const account = order.dcNum + "-" + order.customerNum;
    fetchAndProcessOrder(order.orderId, account).then((o) => {
      if (o) {
        orderDetailsService.announcestartTimer(o);
        setOrderFromResponse(o);
      }
    }).catch(() => {
      setShowErrorDTK(true);
    });
  };

  const refreshCurrentOrder = () => {
    orderDetailsService.resetTimer("reset");
    setShowErrorDTK(false);
    setShowOverRiddenPopUp(false);
    const account = order.dcNum + "-" + order.customerNum;
    fetchAndProcessOrder(order.orderId, account).then((o) => {
      if (o) {
        setOrderFromResponse(o);
      }
    }).catch(() => {
      setShowErrorDTK(true);
    });
  };

  const refreshOrder = () => {
    setShowErrorDTK(false);
    setShowOverRiddenPopUp(false);
    const account = order.dcNum + "-" + order.customerNum;
    fetchAndProcessOrder(order.orderId, account).then((o) => {
      if (o) {
        setOrderFromResponse(o);
      }
    }).catch(() => {
      setShowErrorDTK(true);
    });
  };

  const cancelCsosOrders = (status: string) => {
    const ordersUpdate = new OrdersUpdate();
    ordersUpdate.operationName = status;
    ordersUpdate.orders = [{
      customerNumber: order.customerNum,
      distributionCenter: order.dcNum,
      orderCreateSTP: "",
      orderId: order.orderId,
      uniqueTrackingNumber: "",
      uniqueTrackingNumberOld: "",
    }];
    ordersUpdate.statusCode = order.status;
    csosApiService.updateCsosOrders(ordersUpdate).then((response) => {
      if (response.body && response.body.hostMessage === "SUCCESS") {
        orderDetailsService.announceDetailClose("close");
        refreshOrderSummary();
      } else {
        orderDetailsService.resetTimer("reset");
        orderDetailsService.announceDetailClose("close");
      }
    }).catch(() => {
      orderDetailsService.resetTimer("reset");
      orderDetailsService.announceDetailClose("close");
    });
  };

  const refreshOrderSummary = () => {
    if (order.status === Constants.SIGNED) {
      orderDetailsService.SignedTabRefresh("refreshsigned");
    } else {
      orderDetailsService.refreshorder("refresh");
    }
  };

  const closeOrderDetail = () => {
    orderDetailsService.announceDetailClose("close");
    setShowOverRiddenPopUp(false);
    orderDetailsService.resetTimer("reset");
  };

  const approveQuantity = (status: string) => {
    const orderQuantity = new UpdateQuantity();
    orderQuantity.orderId = order.orderId;
    orderQuantity.distributionCenter = order.dcNum;
    orderQuantity.customerNumber = order.customerNum;
    orderQuantity.orderCreateSTP = order.submitted;
    orderQuantity.operationName = status;
    orderQuantity.statusCode = order.status;
    orderQuantity.orderLines = buildModifiedOrderLines(order.orderLines);

    if (orderQuantity.orderLines.length !== 0) {
      csosApiService.updateQuantity(orderQuantity).then((response) => {
        if (response.body && response.body.hostMessage === "SUCCESS") {
          orderDetailsService.resetTimer("reset");
          if (order.totalNumOfApprovedOrders < 19) {
            fetchNextOrder();
          } else {
            setShowOrderTwenty(true);
          }
        } else {
          setShowErrorDTK(true);
        }
      }).catch(() => {
        setShowErrorDTK(true);
      });
    } else {
      const ordersUpdate = new OrdersUpdate();
      orderDetailsService.resetTimer("reset");
      ordersUpdate.operationName = status;
      ordersUpdate.orders = [{
        customerNumber: order.customerNum,
        distributionCenter: order.dcNum,
        orderCreateSTP: "",
        orderId: order.orderId,
        uniqueTrackingNumber: "",
        uniqueTrackingNumberOld: "",
      }];
      ordersUpdate.statusCode = order.status;
      csosApiService.updateCsosOrders(ordersUpdate).then((response) => {
        if (response.body && response.body.hostMessage === "SUCCESS") {
          if (order.totalNumOfApprovedOrders < 19) {
            fetchNextOrder();
          } else {
            setShowOrderTwenty(true);
          }
        } else {
          setShowErrorDTK(true);
          refreshCurrentOrder();
        }
      }).catch(() => {
        setShowErrorDTK(true);
      });
    }
  };

  const updateQuantityReject = (status: string) => {
    const oq = new UpdateQuantity();
    oq.orderId = order.orderId;
    oq.distributionCenter = order.dcNum;
    oq.customerNumber = order.customerNum;
    oq.operationName = status;
    oq.orderCreateSTP = order.submitted;
    oq.statusCode = order.status;
    oq.orderLines = order.orderLines.map((ol: any) => ({
      availableOrdQty: 0,
      availableOrdQtyOld: ol.oldOrderQuantity,
      cin: ol.cin,
      itemId: ol.itemId,
      originalOrdQty: ol.originalQuantity,
      sequenceNumber: ol.sequence,
    }));
    csosApiService.updateQuantity(oq).then((response) => {
      if (response.body && response.body.hostMessage === "SUCCESS") {
        orderDetailsService.resetTimer("reset");
        fetchNextOrder();
      } else {
        setShowErrorDTK(true);
      }
    }).catch(() => {
      setShowErrorDTK(true);
    });
  };

  const openSignApproveLayer = () => {
    orderDetailsService.resetTimer("reset");
    orderDetailsService.openSignApprove("openSignLayer");
  };

  const closeModal = () => {
    setShowOrderTwenty(false);
    orderDetailsService.announceDetailClose("close");
  };

  const cancelLockedOrder = () => {
    refreshOrderSummary();
    closeOrderDetail();
  };

  const showCancel = (order.status === Constants.NEW && !order.orderLocked) ||
    (!order.orderLocked && order.status === Constants.APPROVED && loggedInUser !== order.updatedBy) ||
    (order.status === Constants.FAILURE && order.axwayFailed) ||
    (order.orderLocked && loggedInUser === order.updatedBy);

  const showReject = (order.status === Constants.NEW && !order.orderLocked) ||
    order.status === Constants.FAILURE ||
    (order.orderLocked && loggedInUser === order.updatedBy);

  const showApprove = (order.status === Constants.NEW && !order.orderLocked) ||
    (order.orderLocked && loggedInUser === order.updatedBy);

  const showApproveSign = (!order.orderLocked && order.status === Constants.NEW) ||
    (order.status === Constants.APPROVED && loggedInUser === order.updatedBy) ||
    (order.status === Constants.FAILURE && order.axwayFailed) ||
    (order.orderLocked && loggedInUser === order.updatedBy);

  const showMoveToNextOrder =
    (order.orderLocked && order.status === Constants.APPROVED && loggedInUser !== order.updatedBy) ||
    (order.orderLocked && order.status === Constants.NEW && loggedInUser !== order.updatedBy);

  const showLockedCancel =
    (order.orderLocked && order.status === Constants.NEW && loggedInUser !== order.updatedBy) ||
    (order.orderLocked && order.status === Constants.APPROVED && loggedInUser !== order.updatedBy);

  const showOverride = !order.orderLocked && order.status === Constants.APPROVED && loggedInUser !== order.updatedBy;

  const showOK = (order.status === Constants.INPROGRESS && order.statusDesc === Constants.OrderINprogress) ||
    order.status === Constants.SIGNED ||
    (order.status === Constants.FAILURE && order.dtkfailed) ||
    (order.status === Constants.APPROVED && loggedInUser === order.updatedBy);

  const isPrimaryApproveSign = (order.status === Constants.APPROVED && loggedInUser === order.updatedBy) ||
    (order.status === Constants.FAILURE && order.axwayFailed);

  const isPrimaryOverride = order.status === Constants.APPROVED && loggedInUser !== order.updatedBy;

  const isPrimaryOK = order.status === Constants.INPROGRESS || order.status === Constants.SIGNED ||
    (order.orderLocked && order.status === Constants.NEW) ||
    (order.orderLocked && loggedInUser !== order.updatedBy && order.status === Constants.NEW) ||
    (order.status === Constants.FAILURE && order.dtkfailed);

  return (
    <div className="footer">
      {showReject && (
        <button type="button" className="btn btn-secondary rejectbutton" onClick={() => setShowRejectModal(true)}>Reject</button>
      )}
      {showCancel && (
        <button type="button" className="btn btn-secondary nonprimary cancelbutton" onClick={newCsosOrders}>Cancel</button>
      )}
      {showOK && (
        <button
          type="button"
          className={`btn btn-secondary nonprimary ${isPrimaryOK ? "primaryaction" : ""}`}
          onClick={() => { refreshOrderSummary(); closeOrderDetail(); }}
        >
          OK
        </button>
      )}
      {showApproveSign && (
        <SignApprovedOrder order={order} setOrder={setOrder} />
      )}
      {showApprove && (
        <button
          type="button"
          className={`btn btn-secondary approvebtn nonprimary ${order.status === Constants.NEW ? "primaryaction" : ""}`}
          onClick={approveCsosOrders}
        >
          Approve
        </button>
      )}
      {showLockedCancel && (
        <button type="button" className="btn btn-secondary nonprimary" onClick={cancelLockedOrder}>Cancel</button>
      )}
       {showMoveToNextOrder && (
        <button
          type="button"
          className="btn btn-secondary moveToNextOrderbtn nonprimary primaryaction"
          onClick={fetchNextOrder}
        >
          Move to Next Order
        </button>
      )}
      {showOverride && (
        <button
          type="button"
          className={`btn btn-secondary nonprimary ${isPrimaryOverride ? "primaryaction" : ""}`}
          onClick={() => setShowOverrideModal(true)}
        >
          Override
        </button>
      )}

      {/* Reject Modal */}
      <ModalContentCenter visible={showRejectModal}>
        <div className="modal-fade" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="footer-modal-header">
                <button type="button" className="close" onClick={() => setShowRejectModal(false)}>&times;</button>
                <h4 className="footer-modal-title confirm-modal-title">Confirm</h4>
              </div>
              <div className="footer-modal-body">
                <p>Please contact the store before you reject this order. Are you sure you want to delete this order?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary cancelbtn nonprimary" onClick={() => setShowRejectModal(false)}>Cancel</button>
                <button type="button" className="btn btn-secondary signorder" onClick={() => { setShowRejectModal(false); rejectCsosOrders(); }}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* Override Modal */}
      <ModalContentCenter visible={showOverrideModal}>
        <div className="modal-fade" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="footer-modal-header">
                <button type="button" className="close" onClick={() => setShowOverrideModal(false)}>&times;</button>
                <h4 className="footer-modal-title">Confirm</h4>
              </div>
              <div className="footer-modal-body">
                <p>Overriding will cancel the previous approval. Are you sure you want to override this order?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary cancelbtn nonprimary" onClick={() => setShowOverrideModal(false)}>Cancel</button>
                <button type="button" className="btn btn-secondary signorder" onClick={() => { setShowOverrideModal(false); overrideCsosOrders(); }}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* Order Twenty Modal */}
      <ModalContentCenter visible={showOrderTwenty}>
        <div className="modal-fade" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="footer-modal-header">
                <button type="button" className="close" onClick={() => { setShowOrderTwenty(false); refreshCurrentOrder(); }}>&times;</button>
                <h4 className="footer-modal-title">Sign Orders</h4>
              </div>
              <div className="footer-modal-body">
                <p>You have reached the maximum number of orders that can be approved at this time.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary signorder" onClick={() => { openSignApproveLayer(); closeModal(); }}>Sign Orders</button>
                <button type="button" className="btn btn-secondary nonprimary" onClick={() => { refreshOrderSummary(); closeModal(); }}>Return to Home Page</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* DTK Error Modal */}
      <ModalContentCenter visible={showErrorDTK}>
        <div className="modal-fade DTKfailed" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content DTKerror">
              <div className="footer-modal-header">
                <button type="button" className="btn btn-secondary close" onClick={() => setShowErrorDTK(false)}>&times;</button>
                <h4 className="footer-modal-title">Alert</h4>
              </div>
              <div className="footer-modal-body DTKerror-body">
                <p className="center">Please reconfirm details and resubmit order.</p>
              </div>
              <div className="modal-footer removegrey dtk-error-button-container">
                <button type="button" className="btn btn-secondary overrideDTK" onClick={() => { setShowErrorDTK(false); refreshOrder(); }}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* Override Already in Process Modal */}
      <ModalContentCenter visible={showOverRiddenPopUp}>
        <div className="modal-fade DTKfailed" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content DTKerror">
              <div className="footer-modal-header">
                <button type="button" className="btn btn-secondary close" onClick={() => { refreshOrderSummary(); closeOrderDetail(); }}>&times;</button>
                <h4 className="footer-modal-title">Alert</h4>
              </div>
              <div className="footer-modal-body">
                <p className="center">"The order is already in process. Please verify."</p>
              </div>
              <div className="modal-footer removegrey">
                <button type="button" className="btn btn-secondary overrideDTK" onClick={() => { refreshOrderSummary(); closeOrderDetail(); }}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>
    </div>
  );
};

export default OrderDetailFooter;


console.log("OrderDetailFooter rendered 1");
console.log("OrderDetailFooter rendered 2");
console.log("OrderDetailFooter rendered 3");
console.log("OrderDetailFooter rendered 4");
console.log("OrderDetailFooter rendered 5");
console.log("OrderDetailFooter rendered 6");
console.log("OrderDetailFooter rendered 7");
console.log("OrderDetailFooter rendered 8");
console.log("OrderDetailFooter rendered 9");
console.log("OrderDetailFooter rendered 10");
console.log("OrderDetailFooter rendered 11");
console.log("OrderDetailFooter rendered 12");
console.log("OrderDetailFooter rendered 13");
console.log("OrderDetailFooter rendered 14");
console.log("OrderDetailFooter rendered 15");
console.log("OrderDetailFooter rendered 16");
console.log("OrderDetailFooter rendered 17");
console.log("OrderDetailFooter rendered 18");
console.log("OrderDetailFooter rendered 19");
console.log("OrderDetailFooter rendered 20");
